import {
  FaFacebookF,
  FaYoutube,
} from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import { HashLink } from 'react-router-hash-link';
import logo from '../assets/logo.png';
import stocktwits from '../assets/partners/partner_3.png';

const IOS_URL = 'https://apps.apple.com/us/app/its-all-about-the-options/id6741738762';
const ANDROID_URL = 'https://play.google.com/apps/internaltest/4701567087476213772';
const APP_STORE_BADGE = `${process.env.PUBLIC_URL}/badges/app-store.svg`;
const PLAY_STORE_BADGE = `${process.env.PUBLIC_URL}/badges/play-store.svg`;

const SOCIAL = [
  {
    href: 'https://youtube.com/@allabouttheoptions4376',
    label: 'YouTube',
    node: <FaYoutube className="h-4 w-4 text-[#FF0000]" />,
  },
  {
    href: 'https://www.facebook.com/groups/652909564891054',
    label: 'Facebook',
    node: <FaFacebookF className="h-4 w-4 text-[#1877F2]" />,
  },
  {
    href: 'https://stocktwits.com/itsallaboutheoptions',
    label: 'Stocktwits',
    node: <img src={stocktwits} alt="" className="h-4 w-4 object-contain" />,
  },
  {
    href: 'https://x.com/aboutheoptions',
    label: 'X',
    node: <FaX className="h-3.5 w-3.5 text-white" />,
  },
];

const Footer = ({
  variant = 'classic',
  homeHash = '/#home',
  aboutHash = '/#about',
  pricingHash = '/#pricing',
  contactHash = '/#contact',
} = {}) => {
  if (variant === 'v2') {
    return (
      <footer className="relative overflow-hidden border-t border-slate-200 bg-[#0b0b10] text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(103,86,254,0.18),transparent_55%)]" />

        <div className="relative mx-auto max-w-6xl px-4 pt-14 pb-8 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
            {/* Brand + app */}
            <div className="lg:col-span-5">
              <div className="flex items-center gap-2.5">
                <img src={logo} alt="" className="h-9 w-9 rounded-lg object-contain" />
                <div>
                  <p className="text-sm font-bold tracking-tight text-white sm:text-base">
                    It's all About the Options
                  </p>
                  <p className="text-[11px] text-slate-400">Alerts · Mentorship · Community</p>
                </div>
              </div>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
                Daily SMS trade ideas with entry, stop, and target — plus order flow context and mentorship.
              </p>

              <div className="mt-6">
                <p className="mb-3 text-[11px] font-bold tracking-[0.22em] text-slate-500 uppercase">
                  Get the app
                </p>
                <div className="flex flex-wrap items-center gap-2.5">
                  <a
                    href={IOS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block transition hover:opacity-90 active:scale-[0.98]"
                  >
                    <img
                      src={APP_STORE_BADGE}
                      alt="Download on the App Store"
                      className="h-10 w-auto"
                      loading="lazy"
                    />
                  </a>
                  <a
                    href={ANDROID_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block transition hover:opacity-90 active:scale-[0.98]"
                  >
                    <img
                      src={PLAY_STORE_BADGE}
                      alt="Get it on Google Play"
                      className="h-10 w-auto"
                      loading="lazy"
                    />
                  </a>
                </div>
              </div>
            </div>

            {/* Links */}
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-7 lg:pl-6">
              <div>
                <p className="mb-3 text-[11px] font-bold tracking-[0.22em] text-slate-500 uppercase">
                  Explore
                </p>
                <ul className="space-y-2.5 text-sm text-slate-300">
                  <li>
                    <HashLink smooth to={homeHash} className="transition hover:text-white">
                      Home
                    </HashLink>
                  </li>
                  <li>
                    <HashLink smooth to={aboutHash} className="transition hover:text-white">
                      About
                    </HashLink>
                  </li>
                  <li>
                    <HashLink smooth to={pricingHash} className="transition hover:text-white">
                      Pricing
                    </HashLink>
                  </li>
                  <li>
                    <HashLink smooth to={contactHash} className="transition hover:text-white">
                      Contact
                    </HashLink>
                  </li>
                </ul>
              </div>

              <div>
                <p className="mb-3 text-[11px] font-bold tracking-[0.22em] text-slate-500 uppercase">
                  Legal
                </p>
                <ul className="space-y-2.5 text-sm text-slate-300">
                  <li>
                    <HashLink smooth to="/terms" className="transition hover:text-white">
                      Terms of Use
                    </HashLink>
                  </li>
                  <li>
                    <HashLink smooth to="/privacy-policy" className="transition hover:text-white">
                      Privacy Policy
                    </HashLink>
                  </li>
                </ul>
              </div>

              <div className="col-span-2 sm:col-span-1">
                <p className="mb-3 text-[11px] font-bold tracking-[0.22em] text-slate-500 uppercase">
                  Social
                </p>
                <div className="flex flex-wrap gap-2">
                  {SOCIAL.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.label}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 transition hover:border-[#6756FE]/40 hover:bg-[#6756FE]/15"
                    >
                      {item.node}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-5 text-center sm:flex-row sm:text-left">
            <p className="text-xs text-slate-500">
              © 2016 It's All About The Options. All rights reserved.
            </p>
            <p className="text-xs text-slate-500">
              Developed and Maintained by{' '}
              <span className="font-medium text-[#6756FE]">IAATO</span>
            </p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-[#0e0e11] text-white pt-16 px-4 md:px-20">
      <div className="text-center mb-10">
        <h3 className="text-2xl font-semibold mb-4">Social Media</h3>
        <div className="flex flex-wrap gap-6 justify-center items-center text-gray-300">
          {SOCIAL.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={item.label}
              className="flex items-center gap-2"
            >
              {item.node}
            </a>
          ))}
        </div>
      </div>

      <hr className="border-gray-700 mb-10" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-16 text-gray-300">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img src={logo} alt="It's all About the Options" className="w-8 h-8" />
            <h4 className="text-xl font-bold">
              <span className="text-indigo-500">It's all About the Options</span>
            </h4>
          </div>
          <p className="text-sm leading-relaxed">
            © 2016 It's All About The Options. All rights reserved. <br />
          </p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            <a href={IOS_URL} target="_blank" rel="noreferrer" className="inline-block hover:opacity-90">
              <img src={APP_STORE_BADGE} alt="Download on the App Store" className="h-10 w-auto" loading="lazy" />
            </a>
            <a href={ANDROID_URL} target="_blank" rel="noreferrer" className="inline-block hover:opacity-90">
              <img src={PLAY_STORE_BADGE} alt="Get it on Google Play" className="h-10 w-auto" loading="lazy" />
            </a>
          </div>
        </div>

        <div>
          <h5 className="font-semibold text-white mb-4">Site Address</h5>
          <ul className="space-y-2 text-sm">
            <li>
              <HashLink smooth to={homeHash} className="hover:text-indigo-400">
                Home
              </HashLink>
            </li>
            <li>
              <HashLink smooth to={aboutHash} className="hover:text-indigo-400">
                About Us
              </HashLink>
            </li>
            <li>
              <HashLink smooth to={pricingHash} className="hover:text-indigo-400">
                Pricing
              </HashLink>
            </li>
            <li>
              <HashLink smooth to={contactHash} className="hover:text-indigo-400">
                Contact
              </HashLink>
            </li>
          </ul>
        </div>

        <div>
          <h5 className="font-semibold text-white mb-4">Site Links</h5>
          <ul className="space-y-2 text-sm">
            <li>
              <HashLink smooth to="/terms" className="hover:text-indigo-400">
                Terms of Use (EULA)
              </HashLink>
            </li>
            <li>
              <HashLink smooth to="/privacy-policy" className="hover:text-indigo-400">
                Privacy policy
              </HashLink>
            </li>
          </ul>
        </div>
      </div>

      <hr className="border-gray-700" />

      <div className="text-center text-sm text-gray-500 py-4">
        Developed and Maintained by <span className="text-indigo-500 hover:underline">IAATO</span>
      </div>
    </footer>
  );
};

export default Footer;
